import { add } from 'date-fns'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export type PendingRange = { startDate: Date | null; endDate: Date | null }

export const getInclusiveDaySpan = (start: Date, end: Date) =>
  Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY) + 1

export const normalizeRangeChronology = (range: PendingRange): PendingRange => {
  if (!range.startDate || !range.endDate) return range
  if (range.startDate.getTime() <= range.endDate.getTime()) return range
  return { startDate: range.endDate, endDate: range.startDate }
}

export const getPendingRangeDaySpan = (range: PendingRange): number | null => {
  const normalized = normalizeRangeChronology(range)
  if (!normalized.startDate || !normalized.endDate) return null
  return getInclusiveDaySpan(normalized.startDate, normalized.endDate)
}

export const isSameDayUTC = (a: Date, b: Date) =>
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate()

export const clampRangeToMinDays = (
  startDate: Date | null,
  endDate: Date | null,
  maxDate: Date,
  minDays: number,
  anchorDate?: Date | null,
) => {
  if (!startDate || !endDate) return { startDate, endDate }

  const normalized = normalizeRangeChronology({ startDate, endDate })
  const start = normalized.startDate!
  const end = normalized.endDate!

  const diffDays = getInclusiveDaySpan(start, end)
  if (diffDays >= minDays) {
    return { startDate: start, endDate: end }
  }

  const anchor =
    anchorDate && anchorDate.getTime() >= start.getTime() && anchorDate.getTime() <= end.getTime()
      ? anchorDate
      : start
  const anchorIsEnd = isSameDayUTC(anchor, end)
  const anchorIsStart = isSameDayUTC(anchor, start)
  if (anchorIsEnd && !anchorIsStart) {
    const minStart = add(anchor, { days: -(minDays - 1) })
    return { startDate: minStart, endDate: anchor }
  }
  const minEnd = add(anchor, { days: minDays - 1 })
  if (minEnd.getTime() <= maxDate.getTime()) {
    return { startDate: anchor, endDate: minEnd }
  }

  const adjustedEnd = maxDate
  const adjustedStart = add(adjustedEnd, { days: -(minDays - 1) })
  return { startDate: adjustedStart, endDate: adjustedEnd }
}

/**
 * Cap an oversized range to maxDays, keeping the newer (end) date fixed —
 * same anchoring behavior as Monthly → Daily period clamping.
 */
export const clampRangeToMaxDays = (range: PendingRange, maxDays: number) => {
  if (!range.startDate || !range.endDate) return range
  const normalized = normalizeRangeChronology(range)
  const start = normalized.startDate!
  const end = normalized.endDate!

  const diffDays = getInclusiveDaySpan(start, end)
  if (diffDays <= maxDays) return { startDate: start, endDate: end }
  return {
    startDate: add(end, { days: -(maxDays - 1) }),
    endDate: end,
  }
}

export const clampRangeToDayBounds = ({
  range,
  maxDate,
  minDays,
  maxDays,
  anchorDate,
}: {
  range: PendingRange
  maxDate: Date
  minDays: number
  maxDays: number
  anchorDate?: Date | null
}) => {
  const maxClampedRange = clampRangeToMaxDays(range, maxDays)
  const minClampedRange = clampRangeToMinDays(
    maxClampedRange.startDate,
    maxClampedRange.endDate,
    maxDate,
    minDays,
    anchorDate ?? maxClampedRange.endDate,
  )
  return clampRangeToMaxDays(minClampedRange, maxDays)
}

const isNewStartSelection = (prev: PendingRange | null, next: PendingRange) => {
  if (!prev?.endDate || !next.startDate || !next.endDate) return false
  const endMatchesPrev = isSameDayUTC(next.endDate, prev.endDate)
  const startChanged = !prev.startDate || !isSameDayUTC(next.startDate, prev.startDate)
  return endMatchesPrev && startChanged
}

/**
 * Resolve a date-range picker change into a stable range and anchor,
 * ensuring min/max-day rules and preserving the user's initial click intent.
 */
export const resolveRangeSelection = ({
  selection,
  previous,
  anchor,
  maxDate,
  minDays,
  maxDays,
}: {
  selection: PendingRange
  previous: PendingRange | null
  anchor: Date | null
  maxDate: Date
  minDays: number
  maxDays: number
}) => {
  const normalized = normalizeRangeChronology(selection)
  if (!normalized.startDate) {
    return { range: normalized, anchor: null }
  }
  if (!normalized.endDate) {
    return { range: normalized, anchor: normalized.startDate }
  }
  const previousIsSingleDay =
    previous?.startDate && previous?.endDate && isSameDayUTC(previous.startDate, previous.endDate)
  const isSingleDay = isSameDayUTC(normalized.startDate, normalized.endDate)
  if (isSingleDay) {
    if (previousIsSingleDay && anchor && !isSameDayUTC(anchor, normalized.startDate)) {
      return {
        range: clampRangeToDayBounds({
          range: { startDate: anchor, endDate: normalized.startDate },
          maxDate,
          minDays,
          maxDays,
          anchorDate: anchor,
        }),
        anchor,
      }
    }
    return {
      range: { startDate: normalized.startDate, endDate: normalized.startDate },
      anchor: normalized.startDate,
    }
  }
  if (!previousIsSingleDay && isNewStartSelection(previous, normalized)) {
    return {
      range: { startDate: normalized.startDate, endDate: normalized.startDate },
      anchor: normalized.startDate,
    }
  }
  const nextAnchor = anchor ?? normalized.endDate
  return {
    range: clampRangeToDayBounds({
      range: normalized,
      maxDate,
      minDays,
      maxDays,
      anchorDate: nextAnchor,
    }),
    anchor: nextAnchor,
  }
}
